import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useCatalogSearch,
  type CatalogProduct,
} from '@/features/search-optimization/catalog-search.api';
import { cn } from '@/lib/utils';

/**
 * Pick one item out of the catalogue.
 *
 * The original mounted its whole search page inside a modal to do this. The
 * search is the same one; only what a row does differs, so this is the search
 * reduced to the part a picker needs and handed a callback.
 */
export function ItemPicker({
  onPick,
  excludeId,
  busy,
}: {
  onPick: (item: CatalogProduct) => void;
  /** The item being edited, which must not be recommended with itself. */
  excludeId?: string | null;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  const [query, setQuery] = useState('');

  const search = useCatalogSearch({
    Query: query,
    Page: 0,
    Size: 20,
    Filters: [],
    Sort: 0,
  });

  const rows = (search.data?.items ?? []).filter(
    (item) => item.id !== excludeId
  );

  return (
    <div className="space-y-3">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(term.trim());
        }}
      >
        <Input
          value={term}
          placeholder={t('itemMaster.searchPlaceholder')}
          onChange={(event) => setTerm(event.target.value)}
        />
        <Button type="submit" variant="outline" disabled={search.isFetching}>
          {search.isFetching ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Search className="size-4" />
          )}
        </Button>
      </form>

      {query === '' && (
        <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
          {t('itemMaster.searchToBegin')}
        </p>
      )}

      {query !== '' && search.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-2.5">
              <Skeleton className="size-10 shrink-0 rounded-lg" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-28" />
              </div>
            </div>
          ))}
        </div>
      )}

      {query !== '' && search.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-10 text-center text-sm">
            {t('itemMaster.noResults')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={search.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              search.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((item) => (
              <button
                key={item.id}
                type="button"
                disabled={busy}
                onClick={() => onPick(item)}
                className="hover:bg-accent flex w-full items-center gap-3 p-2.5 text-left transition-colors disabled:opacity-50"
              >
                {item.itemImage ? (
                  <img
                    src={item.itemImage}
                    alt=""
                    className="size-10 shrink-0 rounded-lg border object-cover"
                  />
                ) : (
                  <div className="bg-muted size-10 shrink-0 rounded-lg border" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {[item.brand, item.name].filter(Boolean).join(' ')}
                  </p>
                  <p className="text-muted-foreground truncate font-mono text-xs">
                    {item.gtin}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
